import React, { useEffect, useMemo, useCallback, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { format } from 'date-fns';
import Header from "../../../../components/Header/Header";
import Footer from "../../../../components/Footer/Footer";
import IndentDetails from "./IndentDetails";
import ItemsTable from "./ItemsTable";
import IndentChangesTracker from "../../IndentChangesTracker";
import PrinterButton from "../../IndentComponents/PrinterButton";
import { IndentPDF } from "../../../../components/Header/IndentPDF";
import axios from "axios";
import { History } from "lucide-react";
import {
  fetchDraftItems,
  fetchCategories,
  fetchChangeLogs,
  saveChangeLog,
  saveDraft,
  submitIndent,
  addItem,
  deleteItem,
  updateItem,
  setError,
} from "../../../../store/invoiceSlice";
import { message } from "antd";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";
// Separate LoadingSkeleton into its own component file
const LoadingSkeleton = React.memo(() => (
  <div className="animate-pulse space-y-4">
    <div className="h-4 bg-gray-200 rounded w-3/4" />
    <div className="space-y-3">
      <div className="h-4 bg-gray-200 rounded" />
      <div className="h-4 bg-gray-200 rounded w-5/6" />
    </div>
  </div>
));

// Custom hook for API data fetching
const useIndentData = (indentId, apiEndpoint) => {
  const [pageData, setPageData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!indentId) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [indentResponse, itemsResponse] = await Promise.all([
          axios.get(apiEndpoint),
          axios.get(`${API_BASE_URL}/viewTable/indentitems/${indentId}`),
        ]);

        const indentData = Array.isArray(indentResponse.data)
          ? indentResponse.data[0]
          : indentResponse.data;

        if (indentData) {
          setPageData({
            ...indentData,
            IndentItems: itemsResponse.data || [],
          });
        }
      } catch (err) {
        setError(err.message);
        setPageData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [indentId, apiEndpoint]);

  return { pageData, isLoading, error };
};

// Custom hook for managing items
const useItemsManagement = (indentId, currentUserId, items) => {
  const dispatch = useDispatch();

  const normalizeItem = (item) => ({
    itemName: item.itemName || item.ItemName,
    quantity: item.quantity || item.Quantity,
    stock: item.stock || item.Stock,
    description: item.description || item.Description,
  });

  const handleItemUpdate = useCallback(
    (updateInfo) => {
      const { type, item, index } = updateInfo;
      const normalizedItem = normalizeItem(item);

      const actions = {
        add: () => {
          dispatch(addItem(item));
          dispatch(
            saveChangeLog({
              indentId,
              userId: currentUserId,
              type: "add",
              item: {
                ...normalizedItem,
                // If you need to look up the item name from somewhere else,
                // you could do it here using the itemID
                itemName: item.itemName || `Item ${item.itemID}`, // Fallback if name not available
              },
            })
          );
        },
        delete: () => {
          dispatch(deleteItem(index));
          dispatch(
            saveChangeLog({
              indentId,
              userId: currentUserId,
              type: "delete",
              item: normalizedItem,
            })
          );
        },
        update: () => {
          dispatch(updateItem({ index, item }));
          dispatch(
            saveChangeLog({
              indentId,
              userId: currentUserId,
              type: "update",
              original: normalizeItem(items[index]),
              updated: normalizedItem,
            })
          );
        },
      };

      actions[type]?.();
    },
    [dispatch, indentId, currentUserId, items]
  );

  return { handleItemUpdate };
};

// Add ItemHistoryButton component at the top level
const ItemHistoryButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="px-4 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2"
  >
    <History size={16} />
    <span>Item History</span>
  </button>
);

const ItemCard = ({ item, className, userId, timestamp }) => (
  <div className={`p-4 rounded mb-2 ${className}`}>
    <div className="mb-2 text-sm text-gray-600">
      <span className="font-medium">User ID: {userId}</span>
      {timestamp && (
        <span className="ml-4">
          {format(new Date(timestamp), 'MMM d, yyyy HH:mm:ss')}
        </span>
      )}
    </div>
    <p>Item: {item.itemName || item.ItemName || 'N/A'}</p>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p>Quantity: {item.quantity || item.Quantity || 0}</p>
        <p>Stock: {item.stock || item.Stock || 0}</p>
      </div>
    </div>
  </div>
);

const InvoicingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { row: initialRow } = location.state || {};
  const [row, setRow] = useState(initialRow);
  const [isMobileView, setIsMobileView] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [pdfError, setPdfError] = useState("");
  const [indentStatus, setIndentStatus] = useState({ l1a: 0, l1: 0, l2: 0, po: 0, ph: 0, so: 0 });

  const {
    items,
    categories,
    changeLogs: storedChangeLogs,
    isDraft,
    isEditable,
    loading,
    error,
    changeLog,
  } = useSelector((state) => state.invoice);

  const currentUserId = useSelector((state) => state.auth.id);
  const apiEndpoint = `${API_BASE_URL}/l2page/indents/${row?.IndentID}`;

  const {
    pageData,
    isLoading: pageDataLoading,
    error: pageError,
  } = useIndentData(row?.IndentID, apiEndpoint);

  const { handleItemUpdate } = useItemsManagement(
    row?.IndentID,
    currentUserId,
    items
  );

  // Validation helper
  const validateItem = useCallback((item) => {
    if (!item.itemID || item.quantity <= 0 || item.stock < 0) {
      throw new Error("Invalid item details");
    }
    return true;
  }, []);

  const handleAddItem = useCallback(
    (newItem) => {
      try {
        validateItem(newItem);
        const itemWithDefaults = {
          ...newItem,
          itemName: newItem.itemName || "Unnamed Item",
        };
        dispatch(addItem(itemWithDefaults));
      } catch (err) {
        dispatch(setError(err.message));
      }
    },
    [dispatch, validateItem]
  );

  const handleSaveDraft = useCallback(async () => {
    if (!row?.IndentID) {
      dispatch(setError("No indent selected"));
      return;
    }

    if (!items.length) {
      dispatch(setError("Please add items before saving draft"));
      return;
    }

    try {
      await dispatch(saveDraft({ indentId: row.IndentID, items })).unwrap();
      navigate("/indent", { state: { message: "Draft saved successfully" } });
    } catch (err) {
      dispatch(setError("Failed to save draft. Please try again."));
    }
  }, [dispatch, row?.IndentID, items, navigate]);

  const handleSubmit = useCallback(async () => {
    if (!items.length) {
      dispatch(setError("Cannot submit an empty indent"));
      return;
    }

    try {
      const result = await dispatch(
        submitIndent({
          indentId: row.IndentID,
          userId: currentUserId,
          items,
        })
      ).unwrap();

      navigate("/peindent", {
        state: {
          message: `Indent submitted successfully. ${result.itemsInserted} items processed.`,
        },
      });
    } catch (err) {
      dispatch(setError(err.message || "Submission failed. Please try again."));
    }
  }, [dispatch, row?.IndentID, currentUserId, items, navigate]);

  // Initial data fetching
  useEffect(() => {
    if (row?.IndentID) {
      console.log('Fetching data for indent:', row.IndentID);
      dispatch(fetchDraftItems(row.IndentID));
      dispatch(fetchChangeLogs(row.IndentID)).then((result) => {
        console.log('Change logs fetch result:', result);
        if (result.error) {
          console.error('Error fetching change logs:', result.error);
        } else {
          console.log('Successfully fetched change logs:', result.payload);
        }
      }).catch(error => {
        console.error('Error in change logs fetch:', error);
      });
      dispatch(fetchCategories());

      // Fetch indent status
      const fetchIndentStatus = async () => {
        try {
          const response = await axios.get(`${API_BASE_URL}/l2page/indents/${row.IndentID}`);
          const indentData = Array.isArray(response.data) ? response.data[0] : response.data;
          console.log('Indent status data:', indentData);
          setIndentStatus({
            l1a: indentData.L1A || 0,
            l1: indentData.L1 || 0,
            l2: indentData.L2 || 0,
            po: indentData.PO || 0,
            ph: indentData.PH || 0,
            so: indentData.IR || 0
          });
        } catch (error) {
          console.error('Error fetching indent status:', error);
        }
      };
      fetchIndentStatus();
    }
  }, [dispatch, row?.IndentID]);

  const hasChanges = useMemo(() => {
    const hasChangeLog = Array.isArray(changeLog) && changeLog.length > 0;
    const hasStoredLogs = Array.isArray(storedChangeLogs) && storedChangeLogs.length > 0;
    console.log('Has changes check:', { hasChangeLog, hasStoredLogs });
    return hasChangeLog || hasStoredLogs;
  }, [changeLog, storedChangeLogs]);

  const allChanges = useMemo(() => {
    console.log('Processing changes...');
    console.log('Change Log:', changeLog);
    console.log('Stored Change Logs:', storedChangeLogs);
    
    const addedItems = [];
    const deletedItems = [];
    const updatedItems = [];

    const processChanges = (changes) => {
      if (!changes || !Array.isArray(changes)) {
        console.log('Invalid changes data:', changes);
        return;
      }
      
      changes.forEach(change => {
        if (!change) {
          console.log('Skipping null change');
          return;
        }
        
        console.log('Processing change:', change);
        
        switch (change.type) {
          case 'add':
            if (change.item) {
              addedItems.push({ 
                ...change.item, 
                userId: change.userId, 
                timestamp: change.timestamp 
              });
            }
            break;
          case 'delete':
            if (change.item) {
              deletedItems.push({ 
                ...change.item, 
                userId: change.userId, 
                timestamp: change.timestamp 
              });
            }
            break;
          case 'update':
            if (change.original && change.updated) {
              updatedItems.push({ 
                original: change.original, 
                updated: change.updated,
                userId: change.userId,
                timestamp: change.timestamp
              });
            }
            break;
          default:
            console.log('Unknown change type:', change.type);
            break;
        }
      });
    };

    processChanges(changeLog);
    processChanges(storedChangeLogs);

    const result = {
      addedItems,
      deletedItems,
      updatedItems
    };
    
    console.log('Processed changes result:', result);
    return result;
  }, [changeLog, storedChangeLogs]);

  // Debug the change logs - moved after hasChanges and allChanges declarations
  useEffect(() => {
    console.log('Current changeLog:', changeLog);
    console.log('Current storedChangeLogs:', storedChangeLogs);
    console.log('Has changes:', hasChanges);
    console.log('All changes:', allChanges);
  }, [changeLog, storedChangeLogs, hasChanges, allChanges]);

  // Memoized total amount calculation
  const totalAmount = useMemo(
    () => items.reduce((acc, item) => acc + (item.total || 0), 0).toFixed(2),
    [items]
  );

  // Add mobile view detection
  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth < 768);
    };

    // Initial check
    checkMobileView();

    // Add event listener for window resize
    window.addEventListener('resize', checkMobileView);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  const fetchIndentData = async () => {
    const indentResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l2page/indents/${row?.IndentID}`
    );
    const indentData = await indentResponse.json();
    const indentDetails = Array.isArray(indentData)
      ? indentData[0]
      : indentData;

    const itemsResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l2page/indentitems/${row?.IndentID}`
    );
    const itemsData = await itemsResponse.json();

    // Fetch status information
    const statusResponse = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/l2page/status/${row?.IndentID}`
    );
    const statusData = await statusResponse.json();
    const status = Array.isArray(statusData) ? statusData[0] : statusData;

    // Format dates consistently
    const formatDate = (dateString) => {
      if (!dateString) return null;
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          console.error('Invalid date:', dateString);
          return null;
        }
        return date.toLocaleDateString('en-US', { 
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
      } catch (error) {
        console.error('Error formatting date:', error);
        return null;
      }
    };

    return {
      indentId: row?.IndentID,
      creationDate: formatDate(indentDetails?.CreationDate),
      PEApprovalDate: formatDate(indentDetails?.PEApprovalDate || indentDetails?.PEApprovedDate),
      L1ApprovedDate: formatDate(indentDetails?.L1ApprovedDate),
      L2ApprovalDate: formatDate(indentDetails?.L2ApprovalDate),
      projectName: indentDetails?.ProjectName || "N/A",
      taskScheduled: indentDetails?.TaskSchedule || "N/A",
      status: indentDetails?.Status || "Active",
      invoicingType: indentDetails?.TypeName || "N/A",
      userName: indentDetails?.UserName || "N/A",
      // Add approval flags
      PO: status?.PO || indentDetails?.PO || 0,
      L1: status?.L1 || indentDetails?.L1 || 0,
      L2: status?.L2 || indentDetails?.L2 || 0,
      items: itemsData.map((item) => ({
        name: item.ItemName,
        category: item.CategoryName,
        au: item.UnitName,
        quantity: item.Quantity,
        quantityReceived: item.QuantityReceived,
        difference: item.Difference,
        remarks: item.Remarks,
        stock: item.Stock,
        description: item.Description || "__",
        promisedDays: item.NormalDays,
        promisedDays2: item.EmergencyDays,
        committedDate: item.CommitedDate,
        promisedDate: item.PromisedDate,
        so: item.IR,
        ph: item.PH,
      })),
    };
  };

  const getPDFComponent = (data) => {
    return <IndentPDF data={data} />;
  };

  const handleRevert = async () => {
    try {
      const response = await axios.post(`${API_BASE_URL}/l2page/indent/${row?.IndentID}/revert`);
      if (response.status === 200) {
        message.success('Indent reverted successfully');
        // Refresh the page or update the state as needed
        window.location.reload();
      }
    } catch (error) {
      console.error('Error reverting indent:', error);
      message.error('Failed to revert indent');
    }
  };

  if (loading || pageDataLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
        <LoadingSkeleton />
        </div>
        <Footer />
      </div>
    );
  }

  const displayError = error || pageError;

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <div className="flex-grow p-2 sm:p-4">
        <div className="mb-4 text-center">
          <h3 className="text-xl font-semibold">View Table</h3>
        </div>

        <div className="flex flex-col gap-4 max-w-7xl mx-auto w-full">
          <div className="bg-white rounded-lg shadow-sm">
            {pageData && (
              <IndentDetails
                row={pageData}
                onUpdate={(updatedData) => {
                  setRow(updatedData);
                }}
              />
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm">
            <div className="p-6">
              <div className="flex justify-between items-center border-b pb-4 mb-4">
                <h2 className="text-xl font-bold text-gray-800">Invoice Items</h2>
                <div className="flex items-center gap-2">
                  {console.log('Current indent status:', indentStatus)}
                  {indentStatus.l1a === 1 && indentStatus.l1 === 0 && (
                    <button
                      onClick={handleRevert}
                      className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 flex items-center"
                    >
                      <History className="w-4 h-4 mr-2" />
                      Revert
                    </button>
                  )}
                  <ItemHistoryButton onClick={() => setShowHistory(true)} />
                  <PrinterButton
                    getPDFComponent={getPDFComponent}
                    filename={`indent-${row?.IndentID}`}
                    fetchData={fetchIndentData}
                    onError={(error) =>
                      setPdfError("Failed to generate PDF. Please try again.")
                    }
                  />
                </div>
              </div>
            <ItemsTable
                indentID={row?.IndentID}
              isDraft={isDraft}
              indentStatus={indentStatus}
                mobileView={isMobileView}
                hideHeader={true}
              />
            </div>
          </div>

          {/* Modal for Item History */}
          {showHistory && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b">
                  <h2 className="text-xl font-bold">Item History</h2>
                  <button
                    onClick={() => setShowHistory(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
                <div className="p-4">
            <IndentChangesTracker
                    indentID={row?.IndentID?.toString()}
                    changes={{
                      addedItems: allChanges.addedItems,
                      deletedItems: allChanges.deletedItems,
                      updatedItems: allChanges.updatedItems
                    }}
              storedChangeLogs={storedChangeLogs}
            />
                </div>
              </div>
            </div>
        )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default React.memo(InvoicingPage);
